/**
 * Input form component.
 * Renders all input fields based on their configuration.
 */

import type { FunctionComponent } from 'preact'
import type { InputConfig } from '../types'
import { isHidden, isUnsupportedWidget } from '../core/inputs'
import { TextInput } from './widgets/TextInput'
import { TextArea } from './widgets/TextArea'
import { Select } from './widgets/Select'
import { Checkbox } from './widgets/Checkbox'
import { NumberInput } from './widgets/NumberInput'
import { FileInput } from './widgets/FileInput'
import { AudioInput } from './widgets/AudioInput'
import { ListInput } from './widgets/ListInput'

/**
 * File-typed inputs. "filejson" is the live type string; "dataurl" and
 * "filevalue" are legacy aliases that persisted app configs and frozen
 * snapshots carry forever. A missed alias degrades the field to a plain text
 * box — silently, which is exactly how the audio input shipped broken — so
 * keep all three, and keep them equal to SINGLE_FILE_TYPES in the app's own
 * InputWidgetRenderer.
 */
const SINGLE_FILE_TYPES = ['filejson', 'dataurl', 'filevalue']
const MULTI_FILE_TYPES = SINGLE_FILE_TYPES.map((type) => `list[${type}]`)

interface InputFormProps {
  inputs: InputConfig[]
  values: Record<string, unknown>
  onChange: (name: string, value: unknown) => void
  showLabels: boolean
  disabled: boolean
}

export const InputForm: FunctionComponent<InputFormProps> = ({
  inputs,
  values,
  onChange,
  showLabels,
  disabled,
}) => {
  return (
    <>
      {inputs
        .filter((input) => input.enabled && !isHidden(input, values))
        .map((input) => (
          <InputField
            key={input.name}
            input={input}
            value={values[input.name]}
            allValues={values}
            onChange={(value) => onChange(input.name, value)}
            showLabel={showLabels}
            disabled={disabled}
          />
        ))}
    </>
  )
}

interface InputFieldProps {
  input: InputConfig
  value: unknown
  /** Every field's value — a dependent dropdown reads its parent's from here. */
  allValues: Record<string, unknown>
  onChange: (value: unknown) => void
  showLabel: boolean
  disabled: boolean
}

const InputField: FunctionComponent<InputFieldProps> = ({
  input,
  value,
  allValues,
  onChange,
  showLabel,
  disabled,
}) => {
  const labelClass = `syv-label${input.required ? ' syv-label-required' : ''}`

  return (
    <div class="syv-field">
      {showLabel && <label class={labelClass}>{input.label}</label>}
      {input.description && (
        <span class="syv-description">{input.description}</span>
      )}
      <WidgetRenderer
        input={input}
        value={value}
        allValues={allValues}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}

interface WidgetRendererProps {
  input: InputConfig
  value: unknown
  allValues: Record<string, unknown>
  onChange: (value: unknown) => void
  disabled: boolean
}

const WidgetRenderer: FunctionComponent<WidgetRendererProps> = ({
  input,
  value,
  allValues,
  onChange,
  disabled,
}) => {
  // File-shaped inputs are decided by TYPE, not by the widget key: an app
  // config may carry no widget at all, and a text box would post a string where
  // the run door expects {filename, mime_type, data}.
  if (input.widget === 'audioinput') {
    return (
      <AudioInput
        value={value}
        onChange={onChange}
        accept={input.accept}
        maxSizeMB={input.max_size_mb}
        disabled={disabled}
      />
    )
  }

  const isMultiFile = MULTI_FILE_TYPES.includes(input.type)
  if (isMultiFile || SINGLE_FILE_TYPES.includes(input.type)) {
    return (
      <FileInput
        value={value}
        onChange={onChange}
        accept={input.accept}
        maxSizeMB={input.max_size_mb}
        multiple={isMultiFile || Boolean(input.multiple)}
        disabled={disabled}
      />
    )
  }

  // The app cannot resolve entities without a signed-in user, and a text box
  // would post a made-up id. Say so rather than degrade silently.
  if (isUnsupportedWidget(input)) {
    return (
      <span class="syv-hint">
        Kan ikke udfyldes i en indlejret widget — feltet kræver en bruger, der er logget
        ind i AgentBase.
      </span>
    )
  }

  // Widget keys are the backend's lowercased class names (Dropdown ->
  // "dropdown", Range -> "range", ListInput -> "listinput"; see widgets.py
  // `widget_type`) — the same keys the app's InputWidgetRenderer matches on.
  const widgetType = input.widget || inferWidgetFromType(input.type)

  switch (widgetType) {
    case 'textarea':
      return (
        <TextArea
          value={(value as string) ?? ''}
          onChange={onChange}
          placeholder={input.label}
          disabled={disabled}
        />
      )

    case 'dropdown':
    case 'dependentdropdown': {
      const choices = getDropdownChoices(input, allValues)
      if (choices.length === 0) {
        return (
          <Select
            value=""
            onChange={onChange}
            options={[]}
            placeholder="Ingen valg tilgængelige"
            disabled
          />
        )
      }
      return (
        <Select
          value={(value as string) ?? ''}
          onChange={onChange}
          options={choices.map((choice) => ({
            value: choice,
            label: choice === '' ? '—' : choice,
          }))}
          placeholder={`Vælg ${input.label}`}
          disabled={disabled}
        />
      )
    }

    case 'listinput':
      return (
        <ListInput
          value={value}
          onChange={onChange}
          itemWidget={input.item_widget}
          itemLabel={input.item_label}
          minItems={input.min_items}
          maxItems={input.max_items}
          disabled={disabled}
        />
      )

    case 'checkbox':
      return (
        <Checkbox
          checked={Boolean(value)}
          onChange={onChange}
          label={input.label}
          disabled={disabled}
        />
      )

    case 'range':
    case 'number':
      return (
        <NumberInput
          value={value as number}
          onChange={onChange}
          placeholder={input.label}
          disabled={disabled}
          min={input.ge ?? undefined}
          max={input.le ?? undefined}
          step={input.step ?? undefined}
        />
      )

    case 'text':
    default:
      return (
        <TextInput
          value={(value as string) ?? ''}
          onChange={onChange}
          placeholder={input.label}
          disabled={disabled}
          type={input.type === 'email' ? 'email' : 'text'}
        />
      )
  }
}

/**
 * Infer widget type from the input type.
 */
function inferWidgetFromType(type: string): string {
  switch (type) {
    case 'boolean':
    case 'bool':
      return 'checkbox'
    case 'integer':
    case 'int':
    case 'float':
    case 'number':
      return 'number'
    case 'text':
      return 'textarea'
    case 'email':
      return 'text'
    default:
      return 'text'
  }
}

/**
 * The choices a dropdown offers right now. A dependent dropdown's list is keyed
 * by its parent field's current value — same rule as the app's
 * getDropdownChoices, so the two forms never disagree on what can be picked.
 */
function getDropdownChoices(input: InputConfig, allValues: Record<string, unknown>): string[] {
  if (input.choices_map && input.depends_on) {
    const parent = String(allValues[input.depends_on] ?? '')
    return input.choices_map[parent] ?? []
  }
  return input.choices ?? []
}
